import { GetState, SetState } from "zustand";

import { NUMERIC_TYPES } from "../../../../../../components/QueryBuilder/constants";

import { GridColumn } from "../../../../schemas/report";

import getNested from "../../../../../../utils/GetNested";

import { ChartType, nineBoxRequiredFields } from "../enums";
import { ReportStore } from "../model";

// extended store to handle charts related data
const chartsStore = (set: SetState<ReportStore>, get: GetState<ReportStore>) => ({
	chartData: {},
	visualizeOptions: {},
	selectedChart: ChartType.Table,
	previousChart: ChartType.Table,
	setVisualizeOptions: (options: Partial<Record<ChartType, any>>, noChange: boolean = false) => {
		const filteredOptions = Object.fromEntries(Object.entries(options).filter(([, v]) => v !== null));
		const setObject = {
			visualizeOptions: {
				...get().visualizeOptions,
				...filteredOptions,
			},
		};
		if (noChange) {
			set(setObject);
		} else {
			set({ ...setObject, hasChanges: true });
		}
	},
	getAvailableCharts: () => {
		// return available charts from currently visible column
		const state = get();
		const columnVisibilityMap = state.getSummaryColumnMetadata();
		const enabledColumns = state.getEnabledGridColumns();
		const visibleColumns: GridColumn[] = enabledColumns.filter((c) =>
			getNested(columnVisibilityMap, [c.field, "isColumnVisible"], false)
		);
		const enabledSummarizeFields = state.getEnabledSummarizeRowsColumns();
		const currentNumericFieldValueTypes = new Set(NUMERIC_TYPES);
		const disabledFieldValueTypes = new Set(["multiple-choice", "multiple-select"]);
		const numericColumnsCount = visibleColumns.filter(
			(c) =>
				(currentNumericFieldValueTypes.has(c.fieldValueType || "") ||
					c.defaultAggregationFunction === "count") &&
				!disabledFieldValueTypes.has(c.fieldValueType || "")
		).length;
		const summarizedByColumnsCount = state.getEnabledSummarizeRowsColumns().length;

		const chartTypes: ChartType[] = [];

		// default table always available
		chartTypes.push(ChartType.Table);

		const reportType = get().report?.type;
		// 9 Box requirements
		if (
			reportType === "reviewReport" &&
			numericColumnsCount >= 2 &&
			enabledSummarizeFields.some((col) => nineBoxRequiredFields.has(col.field.toLowerCase()))
		) {
			chartTypes.push(ChartType.NineBox);
		}

		// Bar, Column, Histogram, Pie Chart requirements
		if (numericColumnsCount > 0 && summarizedByColumnsCount > 0) {
			if (["employeeReport", "goalReport", "surveyReport"].includes(reportType || "")) {
				chartTypes.push(ChartType.BarChart, ChartType.ColumnChart, ChartType.HistogramChart);
			} else {
				chartTypes.push(
					ChartType.BarChart,
					ChartType.ColumnChart,
					ChartType.HistogramChart,
					ChartType.PieChart
				);
			}
		}

		// Scatter Chart requirements
		if (numericColumnsCount >= 2 && summarizedByColumnsCount > 0) {
			chartTypes.push(ChartType.ScatterChart);
		}

		// Trendline Chart
		const hasDateField = enabledSummarizeFields.some((c) => c.fieldValueType === "date");
		if (numericColumnsCount > 0 && hasDateField) {
			chartTypes.push(ChartType.TrendlineChart);
		}

		return chartTypes;
	},
	setSelectedChart: (selectedChart) => {
		const previousSelectedChart = get().selectedChart;
		const previousPreviousSelectedChart = get().previousChart;
		if (selectedChart === ChartType.Table) {
			set({ selectedChart, previousChart: previousSelectedChart });
		} else {
			set({
				selectedChart,
				previousChart:
					previousSelectedChart === ChartType.Table
						? previousPreviousSelectedChart === ChartType.Table
							? selectedChart
							: previousPreviousSelectedChart
						: previousSelectedChart,
			});
		}
	},
	fallbackToTableChart: () => {
		set({ selectedChart: ChartType.Table, previousChart: get().selectedChart });
	},
	getSavedVisualizeOptions: () => {
		const visualizeOptions = get().visualizeOptions;
		const savedVisualizeOptions = JSON.parse(JSON.stringify(visualizeOptions));

		for (const [key, value] of Object.entries(visualizeOptions)) {
			if (value && typeof value === "object") {
				for (const [nestedKey, nestedValue] of Object.entries(
					value as Record<string, { hasSuggestedMin?: boolean; hasSuggestedMax?: boolean }>
				)) {
					if (nestedValue === null || typeof nestedValue !== "object") {
						continue;
					}
					if ("hasSuggestedMin" in nestedValue || "hasSuggestedMax" in nestedValue) {
						savedVisualizeOptions[key][nestedKey].hasSuggestedMin = true;
						savedVisualizeOptions[key][nestedKey].hasSuggestedMax = true;
					}
				}
			}
		}

		return savedVisualizeOptions;
	},
});

export default chartsStore;
